'use client';

import { useParams, useSearchParams } from 'next/navigation';

import {
  ClassCanceledPage,
  ClassDetails,
  ClassDetailsSkeleton,
  EmptyCalendarState,
  PageContainer,
} from '@/components';
import { INSTRUCTOR_PARAM } from '@/components/instructor-bio-panel';
import { isCanceledClassError, isOneTimeClass } from '@/helpers/class';
import useAppearanceSettings from '@/state/appearance-settings';
import useClass from '@/state/class';
import { useNotFoundRedirect } from '@/hooks/use-not-found-redirect';
import { useProductViewTracking } from '@/hooks/use-product-view-tracking';
import { readGuestCountParam } from '@/helpers/guest-pass';

const ClassData = () => {
  const params = useParams();
  const slug = params.slug as string;
  const queryParams = useSearchParams();
  const rescheduling = queryParams.get('action') === 'reschedule';
  const orderId = queryParams.get('orderId');
  const slotId = queryParams.get('slotId');
  const instructorScheduleId = queryParams.get('instructorScheduleId');
  const currentStartTime = queryParams.get('startTime');
  const activeQuantityParam = Number(queryParams.get('activeQuantity'));
  const activeQuantity =
    Number.isFinite(activeQuantityParam) && activeQuantityParam > 0
      ? activeQuantityParam
      : 1;
  const rescheduleGuests = readGuestCountParam(queryParams.get('guests'));
  const isRescheduling =
    rescheduling && !!orderId && !!slotId && !!instructorScheduleId;

  const { data: appearanceSetting, isLoading: isAppearanceLoading } =
    useAppearanceSettings();
  const classSettings = appearanceSetting?.class;
  const {
    data: classDetails,
    isLoading: isClassDetailsLoading,
    error,
  } = useClass({ slug });
  const isCanceled = isCanceledClassError(error);
  const isGone = useNotFoundRedirect(isCanceled ? null : error, '/classes');

  useProductViewTracking({
    type: 'class',
    id: classDetails?.class_id,
    slug: classDetails?.slug ?? '',
    name: classDetails?.name ?? '',
    price: classDetails?.price ?? null,
    currency: classDetails?.currency ?? null,
  });

  if (isAppearanceLoading || isClassDetailsLoading) {
    return <ClassDetailsSkeleton />;
  }

  if (isCanceled) return <ClassCanceledPage />;

  if (isGone) {
    return null;
  }

  if (classSettings && classDetails && classDetails?.class_id) {
    return (
      <ClassDetails
        classData={classDetails}
        classSettings={classSettings}
        // Reschedule mode hides the booked time, and a one-time class has no
        // other, so its page would read closed.
        isRescheduling={isRescheduling && !isOneTimeClass(classDetails)}
        orderId={orderId}
        slotId={slotId}
        instructorScheduleId={instructorScheduleId}
        currentStartTime={currentStartTime}
        rescheduleActiveQuantity={activeQuantity}
        rescheduleGuests={rescheduleGuests}
        openInstructorId={queryParams.get(INSTRUCTOR_PARAM)}
      />
    );
  }

  return (
    <PageContainer className="ground-empty-state flex items-center justify-center">
      <EmptyCalendarState
        title="We couldn't load this class"
        description="Please try again."
      />
    </PageContainer>
  );
};

export default ClassData;
